import * as signalR from "@microsoft/signalr";
import Cookies from "js-cookie";
import socketService from '../Services/SocketService';

// SignalR Log Utility
let signalRLogger = null;

export const setSignalRLogger = (logger) => {
  signalRLogger = logger;
};

export const logSignalR = (message, type = 'info') => {
  if (signalRLogger) {
    signalRLogger(message, type);
  }
  // Removed console fallback for cleaner production code
};

/**
 * Creates a SignalR connection with optimized settings for preventing timeout errors
 * @param {string} hubName - The hub name (e.g., 'MainHub', 'ChatHub')
 * @param {Object} options - Additional options
 * @returns {Promise<signalR.HubConnection>} The configured SignalR connection
 */
export const createSignalRConnection = async (hubName, options = {}) => {
  try {
    let hubUrl;
    
    if (hubName === 'MainHub') {
      hubUrl = await socketService.getMainHubUrl();
    } else if (hubName === 'ChatHub') {
      hubUrl = await socketService.getChatHubUrl();
    } else {
      throw new Error(`Unknown hub: ${hubName}`);
    }

    logSignalR(`🔌 Creating SignalR connection for ${hubName}: ${hubUrl}`);

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
        accessTokenFactory: () => Cookies.get("AccessToken"),
        ...options
      })
      .configureLogging(signalR.LogLevel.None)
      .build();

    return connection;
  } catch (error) {
    logSignalR(`❌ Error creating SignalR connection for ${hubName}: ${error.message}`, 'error');
    throw error;
  }
};

/**
 * Starts a ping mechanism to keep the SignalR connection alive
 * @param {signalR.HubConnection} connection - The SignalR connection
 * @param {number} interval - Ping interval in milliseconds (default: 10000)
 * @returns {number} The interval ID for cleanup
 */
export const startPingMechanism = (connection, interval = 10000) => {
  return setInterval(() => {
    if (connection && connection.state === signalR.HubConnectionState.Connected) {
      connection.invoke("Heartbeat")
        .catch((error) => {
          logSignalR(`Heartbeat failed: ${error.message}`, 'warning');
        });
    }
  }, interval);
};

/**
 * Sets up connection state handlers for a SignalR connection
 * @param {signalR.HubConnection} connection - The SignalR connection
 * @param {Object} handlers - Event handlers
 */
export const setupConnectionHandlers = (connection, handlers = {}) => {
  const {
    onReconnecting,
    onReconnected,
    onClose,
    onError,
  } = handlers;

  if (onReconnecting) {
    connection.onreconnecting(onReconnecting);
  }

  if (onReconnected) {
    connection.onreconnected(onReconnected);
  }

  if (onClose) {
    connection.onclose(onClose);
  }

  if (onError) {
    connection.onerror(onError);
  }
};

/**
 * Safely stops a SignalR connection and cleans up resources
 * @param {signalR.HubConnection} connection - The SignalR connection
 * @param {number} pingIntervalId - The ping interval ID to clear
 */
export const stopConnection = (connection, pingIntervalId = null) => {
  if (pingIntervalId) {
    clearInterval(pingIntervalId);
  }
  
  if (connection) {
    connection.stop();
  }
};

/**
 * Checks if a SignalR connection is in a connected state
 * @param {signalR.HubConnection} connection - The SignalR connection
 * @returns {boolean} True if connected
 */
export const isConnected = (connection) => {
  return connection && connection.state === signalR.HubConnectionState.Connected;
};

/**
 * Gets the connection state as a string
 * @param {signalR.HubConnection} connection - The SignalR connection
 * @returns {string} The connection state
 */
export const getConnectionState = (connection) => {
  if (!connection) return 'Disconnected';
  
  switch (connection.state) {
    case signalR.HubConnectionState.Connected:
      return 'Connected';
    case signalR.HubConnectionState.Connecting:
      return 'Connecting';
    case signalR.HubConnectionState.Disconnected:
      return 'Disconnected';
    case signalR.HubConnectionState.Reconnecting:
      return 'Reconnecting';
    default:
      return 'Unknown';
  }
};

/**
 * Gets detailed connection information for debugging
 * @param {signalR.HubConnection} connection - The SignalR connection
 * @returns {Object} Connection details
 */
export const getConnectionDetails = (connection) => {
  if (!connection) {
    return {
      state: 'Disconnected',
      connectionId: null,
      serverTimeout: null,
      keepAliveInterval: null,
      transport: null,
      url: null
    };
  }

  return {
    state: getConnectionState(connection),
    connectionId: connection.connectionId,
    serverTimeout: connection.serverTimeoutInMilliseconds,
    keepAliveInterval: connection.keepAliveIntervalInMilliseconds,
    transport: connection.connection?.transport?.name || 'Unknown',
    url: connection.baseUrl || connection.url || 'Unknown'
  };
};

/**
 * Logs detailed connection information for debugging
 * @param {signalR.HubConnection} connection - The SignalR connection
 * @param {string} context - Context where this is being called
 */
export const logConnectionDetails = (connection, context = '') => {
  const details = getConnectionDetails(connection);
  logSignalR(`SignalR Connection Details ${context ? `(${context})` : ''}: ${JSON.stringify(details)}`);
};

/**
 * Checks if the connection is healthy
 * @param {signalR.HubConnection} connection - The SignalR connection
 * @returns {boolean} True if connection is healthy
 */
export const isConnectionHealthy = (connection) => {
  if (!connection) return false;
  
  const state = connection.state;
  const isConnected = state === signalR.HubConnectionState.Connected;
  const hasConnectionId = !!connection.connectionId;
  
  return isConnected && hasConnectionId;
};

/**
 * Performs a connection health check
 * @param {signalR.HubConnection} connection - The SignalR connection
 * @returns {Object} Health check results
 */
export const performHealthCheck = (connection) => {
  const details = getConnectionDetails(connection);
  const isHealthy = isConnectionHealthy(connection);
  
  const healthCheck = {
    isHealthy,
    details,
    timestamp: new Date().toISOString(),
    issues: []
  };

  if (!isHealthy) {
    if (details.state === 'Disconnected') {
      healthCheck.issues.push('Connection is disconnected');
    }
    if (!details.connectionId) {
      healthCheck.issues.push('No connection ID');
    }
    if (details.state === 'Reconnecting') {
      healthCheck.issues.push('Connection is reconnecting');
    }
  }

  return healthCheck;
};

/**
 * Sets up common method handlers for MainHub connections
 * @param {signalR.HubConnection} connection - The SignalR connection
 * @param {string} context - Context where this is being called (for logging)
 */
export const setupMainHubMethodHandlers = (connection, context = '') => {
  if (!connection) {
    logSignalR(`Cannot setup MainHub method handlers: connection is null ${context ? `(${context})` : ''}`, 'warning');
    return;
  }

  // Handle checkactivetrip method call from server
  connection.on("checkactivetrip", () => {
    // This method is called by the server to check active trips
    // We can implement trip checking logic here if needed
    logSignalR(`${context ? context + ' ' : ''}checkactivetrip method called by server`);
  });

  // Handle alllocation method call from server
  connection.on("alllocation", (data) => {
    // This method is called by the server to send location data
    logSignalR(`${context ? context + ' ' : ''}alllocation method called by server with data:`, data);
  });

  logSignalR(`MainHub method handlers setup complete ${context ? `(${context})` : ''}`);
};